import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { selectChallenges } from 'store/challenges/slice'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { FormattedMessage, useIntl } from 'react-intl'
import Popover from '@mui/material/Popover'
import SimulatorIcon from '@mui/icons-material/PersonalVideo'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import ChallengesContainer from 'components/layout/ChallengesContainer'
import LoadMoreButton from 'components/ui/LoadMoreButton'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import StatusBadge from 'components/challenge/StatusBadge'
import { selectChallenge } from 'store/challenge/slice'
import { getChallenges } from 'store/challenges/thunks'
import { cloneChallenge, deleteChallenge } from 'store/challenge/thunks'
import { selectLocale } from 'store/locale/slice'
import { ChallengeComputedState, getChallengeTypeName } from 'utils/challenges'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { Button } from '@mui/material'
import { ChallengeCategory } from 'api/types'
import { EditPencilIcon } from 'assets/icons/edit-pencil-icon'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { DeleteIcon } from 'assets/icons/deleteIcon'

export default function Challenges(): JSX.Element {
  const location = useLocation()
  const currentChallengesPage = location.pathname === '/admin/challenges'
  const { challenges, status, loadingMoreStatus, shouldRequestMore } = useSelector(selectChallenges)
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const intl = useIntl()
  const [rowMenu, setRowMenu] = useState<string | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [filteredChallenges, setFilteredChallenges] = useState<Challenge[]>([])
  const challenge = useSelector(selectChallenge)
  const { status: jobStatus } = challenge

  useEffect(() => {
    currentChallengesPage
      ? dispatch(
          getChallenges({
            category: [ChallengeCategory.SPONSORED],
            challengeComputedState: [
              ChallengeComputedState.DRAFT,
              ChallengeComputedState.LIVE,
              ChallengeComputedState.UNPUBLISHED,
              ChallengeComputedState.UPCOMING,
              ChallengeComputedState.VISIBLE,
            ],
          }),
        )
      : dispatch(
          getChallenges({
            category: [ChallengeCategory.SPONSORED],
            challengeComputedState: [ChallengeComputedState.COMPLETED],
          }),
        )
  }, [dispatch, currentChallengesPage])

  useEffect(() => {
    if (challenges.length > 0) {
      setFilteredChallenges(challenges)
    }
  }, [challenges])

  const loadNextChallenges = () => {
    dispatch(getChallenges({ category: [ChallengeCategory.SPONSORED] }))
  }

  const loadAllChallenges = () => {
    dispatch(getChallenges({ category: [ChallengeCategory.SPONSORED] }))
  }

  const handleRowMenuOpen = (event: any, id: string) => {
    setRowMenu(id)
    setAnchorEl(event.currentTarget)
  }

  const handleRowMenuClose = () => {
    setRowMenu(null)
    setAnchorEl(null)
  }

  const handleChallengeDuplicate = (id: string) => {
    dispatch(cloneChallenge(id))
    setRowMenu(null)
    setAnchorEl(null)
  }

  const handleChallengeDelete = (id: string) => {
    const confirmed = window.confirm('Do you really want to delete this challenge?')
    if (confirmed) {
      dispatch(deleteChallenge(id))
    }
  }

  const handleSearchChange = (e: any) => {
    const searchTerm = e.target.value.toLowerCase()
    if (searchTerm.length >= 3) {
      const filtered = challenges.filter((c: Challenge) => {
        return (
          c.info?.name?.toLowerCase().includes(searchTerm) ||
          c.info?.subtitle?.content?.toLowerCase().includes(searchTerm)
        )
      })
      if (filtered !== filteredChallenges) {
        setFilteredChallenges(filtered)
      }
    } else if (searchTerm.length === 0) {
      setFilteredChallenges(challenges)
    }
  }

  const isSimulatorChallenge = (challenge: Challenge) =>
    challenge.roundEligibility?.roundTypes?.includes('GOLF_ROUND_TYPE_SIMULATOR_ROUND')

  const columns: GridColDef<Challenge>[] = [
    {
      field: 'name',
      headerName: intl.formatMessage({ id: 'strings.name' }),
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => (
        <>
          {isSimulatorChallenge(row) && <SimulatorChallenge />}
          <Link to={`/admin/challenges/${row.challengeId}`}>{row.info?.name}</Link>
        </>
      ),
    },
    {
      field: 'status',
      headerName: intl.formatMessage({ id: 'strings.status' }),
      minWidth: 125,
      flex: 0.5,
      valueGetter: (_, row) => {
        switch (row.computedState as ChallengeComputedState) {
          case ChallengeComputedState.LIVE:
            return 1
          case ChallengeComputedState.UPCOMING:
            return 2
          case ChallengeComputedState.DRAFT:
            return 3
          case ChallengeComputedState.UNPUBLISHED:
            return 4
          case ChallengeComputedState.VISIBLE:
            return 5
          default:
            return 6
        }
      },
      renderCell: ({ row }) => (
        <div>
          <StatusBadge challengeState={row.computedState} />
        </div>
      ),
    },
    {
      field: 'type',
      headerName: intl.formatMessage({ id: 'challenges.type' }),
      minWidth: 200,
      flex: 1,
      renderCell: ({ row }) => {
        if (!row.typeConfig?.type) {
          return ''
        }
        return getChallengeTypeName(row.typeConfig.type, intl)
      },
    },
    {
      field: 'location',
      headerName: intl.formatMessage({ id: 'strings.location' }),
      minWidth: 100,
      flex: 0.5,
      renderCell: ({ row }) => {
        return (row.roundEligibility?.countries?.length ?? 0) > 0
          ? intl.formatMessage({ id: 'strings.custom' })
          : intl.formatMessage({ id: 'strings.global' })
      },
    },
    {
      field: 'targeting',
      headerName: intl.formatMessage({ id: 'challenges.targeting' }),
      minWidth: 125,
      flex: 0.5,
      renderCell: ({ row }) => {
        return row.audienceControl?.joinAudience?.countries?.length ?? 0 > 0
          ? intl.formatMessage({ id: 'strings.custom' })
          : intl.formatMessage({ id: 'strings.global' })
      },
    },
    {
      field: 'participantCount',
      headerName: intl.formatMessage({ id: 'strings.joined' }),
      minWidth: 125,
      flex: 0.5,
      renderCell: ({ row }) => {
        return row.participantCount ?? 0
      },
    },
    {
      field: 'startAt',
      headerName: intl.formatMessage({ id: 'challengeList.startAt' }),
      sortable: false,
      minWidth: 125,
      flex: 0.5,
      renderCell: ({ row }) => {
        if (!row.schedule?.startAt) {
          return ''
        }
        const dateTime = new Date(row.schedule.startAt)
        return new Intl.DateTimeFormat(locale.language.code).format(dateTime)
      },
    },
    {
      field: 'endAt',
      headerName: intl.formatMessage({ id: 'challengeList.endAt' }),
      sortable: false,
      minWidth: 125,
      flex: 0.5,
      renderCell: ({ row }) => {
        if (!row.schedule?.endAt) {
          return ''
        }
        const dateTime = new Date(row.schedule.endAt)
        return new Intl.DateTimeFormat(locale.language.code).format(dateTime)
      },
    },
    {
      field: 'options',
      sortable: false,
      width: 100,
      flex: 0.3,
      align: 'left',
      renderHeader: () => <span />,
      renderCell: ({ row }) => (
        <div style={{ position: 'relative' }}>
          <Button
            color="primary"
            startIcon={<MoreHorizIcon sx={{ width: '32px', height: '32px' }} />}
            aria-controls="row-menu"
            aria-haspopup="true"
            onClick={(e) => handleRowMenuOpen(e, row.challengeId || '0')}
            sx={{ padding: 0, minWidth: '48px', '& .MuiButton-startIcon': { marginLeft: 0 } }}
          />
          <RowMenu
            open={row.challengeId === rowMenu}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handleRowMenuClose}
            disableRestoreFocus
          >
            {jobStatus === 'loading' ? (
              <LoadingSpinner />
            ) : (
              <>
                <Link to={`/admin/challenges/${row.challengeId}/edit`} className="row-menu-action">
                  <EditPencilIcon />
                  <FormattedMessage id="strings.edit" />
                </Link>
                <div onClick={() => handleChallengeDuplicate(row.challengeId || '0')} className="row-menu-action">
                  <ContentCopyIcon sx={{ width: '16px', height: '16px' }} />
                  <FormattedMessage id="strings.duplicate" />
                </div>
                <div onClick={() => handleChallengeDelete(row.challengeId || '0')} className="row-menu-action">
                  <DeleteIcon />
                  <FormattedMessage id="strings.delete" />
                </div>
              </>
            )}
          </RowMenu>
        </div>
      ),
    },
  ]

  return (
    <ChallengesContainer handleSearchChange={handleSearchChange}>
      <>
        {status === 'loading' ? (
          <SpinnerContainer>
            <LoadingSpinnerGreen size={72} />
          </SpinnerContainer>
        ) : (
          <>
            <DataGridContainer style={{ width: 'calc(100vw - 250px)', padding: '0 8px 0 0' }}>
              <DataGrid
                rows={filteredChallenges}
                getRowId={(row) => row.challengeId ?? ''}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                autoHeight
                initialState={{
                  pagination: { paginationModel: { pageSize: 50 } },
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root, .MuiDataGrid-cell, .MuiDataGrid-columnHeader': {
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-topContainer': {
                    borderTop: '1px solid var(--DataGrid-rowBorderColor)',
                  },
                }}
              />
            </DataGridContainer>
            {!currentChallengesPage && shouldRequestMore && (
              <CustomFooter>
                <>
                  <LoadMoreButton onClick={loadNextChallenges} status={loadingMoreStatus} />
                  <div>
                    <span onClick={loadAllChallenges}>
                      <FormattedMessage id="strings.loadAll" />
                    </span>
                  </div>
                </>
              </CustomFooter>
            )}
          </>
        )}
      </>
    </ChallengesContainer>
  )
}

const RowMenu = styled(Popover)`
  & > .MuiPaper-root {
    padding: 10px;
    color: #000000;

    & .row-menu-action {
      display: flex;
      padding: 8px 10px;
      align-items: center;
      gap: 10px;
      align-self: stretch;
      cursor: pointer;
      text-decoration: none;
      color: #000000;
      &:hover {
        opacity: 0.8;
      }
    }

    & a,
    div {
      display: block;
      cursor: pointer;
      text-decoration: none;
      padding: 5px 0;
      &:hover {
        opacity: 0.8;
      }
      color: #000000;
    }
    border-radius: 8px;
  }

  & > div[tabindex='0']:not(.MuiPaper-root) {
    visibility: hidden;
  }
`

const DataGridContainer = styled.div`
  overflow-x: auto;
  & .MuiDataGrid-columnsContainer {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.customPalette.light};
    font-size: 0.85em;
  }
  & .MuiDataGrid-columnSeparator {
    display: none;
  }
  & a {
    color: ${(props) => props.theme.palette.primary.main};
  }
`

const CustomFooter = styled.div`
  width: 100%;
  height: 52px;
  border: 1px solid rgba(224, 224, 224, 1);
  display: block;
  margin: auto;
  padding: 10px 0 20px 0;
  text-align: center;

  & > div {
    margin-top: 5px;
    & > span {
      cursor: pointer;
      color: ${(props) => props.theme.palette.primary.main};
    }
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const SimulatorChallenge = styled(SimulatorIcon)`
  color: ${(props) => props.theme.palette.primary.main};
  margin-right: 10px;
`
